// src/pages/admin/Page_Dashboard/Page_DashboardInstitutions.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Row,
  Col,
  Table,
  Tag,
  Tooltip,
  Input,
  Select,
  Space,
  message,
} from "antd";
import { PageHeader } from "../../../ui";
import { useAuth } from "../../../hooks/useAuth";
import { dashboardApi } from "../../../api/dashboardAPI";
import dayjs from "dayjs";

function MiniTotals({ totals = {}, loading }) {
  const cards = [
    { title: "Instituciones", value: totals.instituciones ?? 0 },
    { title: "Profesores", value: totals.profesores ?? 0 },
    { title: "Alumnos", value: totals.alumnos ?? 0 },
  ];
  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
      {cards.map((c) => (
        <Col key={c.title} xs={24} sm={12} md={8}>
          <Card
            loading={loading}
            bordered
            hoverable
            style={{ borderRadius: 10 }}
          >
            <div style={{ fontSize: 12, color: "#6b7280" }}>{c.title}</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{c.value}</div>
            <div style={{ fontSize: 12, color: "#9ca3af" }}>
              Agregados sobre el listado
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

export function Page_DashboardInstitutions() {
  const { accessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);

  // filtros locales
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState(undefined);
  const [statusFilter, setStatusFilter] = useState(undefined); // "activo" | "expirado"

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await dashboardApi.getInstitutionsList(accessToken);
        if (alive) setPayload(data);
      } catch (e) {
        const msg =
          e?.body?.message || e?.message || "Error cargando instituciones";
        if (alive) {
          setError(msg);
          message.error(msg);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken]);

  // ✅ items estable para dependencias de useMemo
  const items = useMemo(
    () => (Array.isArray(payload?.items) ? payload.items : []),
    [payload],
  );

  // ✅ totals memoizado (usa items estable)
  const totals = useMemo(() => {
    if (payload?.totals) return payload.totals;
    const profesores = items.reduce(
      (a, it) => a + (Number(it.profesores) || 0),
      0,
    );
    const alumnos = items.reduce((a, it) => a + (Number(it.alumnos) || 0), 0);
    return { instituciones: items.length, profesores, alumnos };
  }, [payload, items]);

  const planOptions = useMemo(() => {
    const set = new Set(items.map((i) => i?.planEduca?.title).filter(Boolean));
    return Array.from(set).map((t) => ({ label: t, value: t }));
  }, [items]);

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase();
    return items.filter((i) => {
      const matchesText =
        !q ||
        (i?.nombre || "").toLowerCase().includes(q) ||
        (i?.telefono || "").toLowerCase().includes(q);

      const matchesPlan = !planFilter || i?.planEduca?.title === planFilter;

      const exp = i?.expired_at ? dayjs(i.expired_at) : null;
      const activo = exp ? dayjs().isBefore(exp) : true;
      const matchesStatus =
        !statusFilter ||
        (statusFilter === "activo" && activo) ||
        (statusFilter === "expirado" && !activo);

      return matchesText && matchesPlan && matchesStatus;
    });
  }, [items, search, planFilter, statusFilter]);

  const columns = [
    {
      title: "Institución",
      dataIndex: "nombre",
      sorter: (a, b) => (a?.nombre || "").localeCompare(b?.nombre || ""),
      render: (v, row) => (
        <Space>
          {/* avatar opcional, mostramos inicial si no hay */}
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              background: "#e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
            }}
            aria-label={v || "Sin logotipo"}
          >
            {row?.avatar && row.avatar !== "null" ? (
              <img
                src={row.avatar}
                alt={v || "avatar"}
                style={{ width: 30, height: 30, borderRadius: "50%" }}
              />
            ) : (
              (v || "?").slice(0, 1).toUpperCase()
            )}
          </div>
          <span style={{ fontWeight: 600 }}>{v || "—"}</span>
        </Space>
      ),
    },
    {
      title: "Dirección",
      dataIndex: "direccion",
      render: (v) =>
        v ? (
          <Tooltip title={v}>
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "inline-block",
                maxWidth: 260,
              }}
            >
              {v}
            </span>
          </Tooltip>
        ) : (
          "—"
        ),
      width: 300,
    },
    {
      title: "Teléfono",
      dataIndex: "telefono",
      width: 140,
      render: (v) => v || "—",
    },
    {
      title: "Plan",
      dataIndex: ["planEduca", "title"],
      render: (_, row) => (
        <div>
          <Tag>{row?.planEduca?.title || "—"}</Tag>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            Cupos: {row?.planEduca?.teacherNumber ?? "—"} docentes /{" "}
            {row?.planEduca?.studentsNumber ?? "—"} alumnos
          </div>
        </div>
      ),
    },
    {
      title: "Profesores",
      dataIndex: "profesores",
      align: "right",
      width: 110,
    },
    { title: "Alumnos", dataIndex: "alumnos", align: "right", width: 110 },
    {
      title: "Alta",
      dataIndex: "created_at",
      width: 120,
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—"),
    },
    {
      title: "Expira",
      dataIndex: "expired_at",
      width: 160,
      render: (v) => {
        const exp = v ? dayjs(v) : null;
        const activo = exp ? dayjs().isBefore(exp) : true;
        return (
          <Space>
            <span>{v ? dayjs(v).format("DD/MM/YYYY") : "—"}</span>
            <Tooltip title={v || ""}>
              <Tag color={activo ? "green" : "red"}>
                {activo ? "Activo" : "Expirada"}
              </Tag>
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: "Partner",
      dataIndex: "partner",
      width: 160,
      render: (v) => v || "—",
    },
    {
      title: "Acción",
      key: "ver",
      fixed: "right",
      width: 90,
      render: (_, row) => (
        <a
          href={`/#/admin/dashboard/institutions/${row._id}`}
          aria-label={`Ver institución ${row?.nombre || ""}`}
        >
          Ver
        </a>
      ),
    },
  ];

  return (
    <div className="page-dashboard">
      <PageHeader
        title="Instituciones"
        subtitle="Listado completo de instituciones"
      />

      <MiniTotals totals={totals} loading={loading} />

      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          allowClear
          placeholder="Buscar por nombre o teléfono…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 260 }}
        />
        <Select
          allowClear
          placeholder="Filtrar por plan"
          value={planFilter}
          onChange={setPlanFilter}
          options={planOptions}
          style={{ width: 220 }}
        />
        <Select
          allowClear
          placeholder="Estado"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { label: "Activo", value: "activo" },
            { label: "Expirado", value: "expirado" },
          ]}
          style={{ width: 160 }}
        />
      </Space>

      <Table
        rowKey={(r) => r._id}
        loading={loading}
        dataSource={filtered}
        columns={columns}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 1200 }}
        locale={{
          emptyText: error ? "No se pudo cargar la información" : "Sin datos",
        }}
      />
    </div>
  );
}
